import { PayoutDto, PayoutFilter, PayoutStatusEnum, PayoutTypeEnum } from '@/app/common/general-types/payouts.dto';
import { Component, computed, effect, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormField, form } from '@angular/forms/signals';
import { debounceTime, distinctUntilChanged, finalize, map, skip, Subscription } from 'rxjs';
import { AdminFilter } from '../../services/admin-filter/admin-filter';
import { ActiveFiltersModel, FilterCreatorsDto } from '@/app/common/general-types/filters.dto';
import { PayoutsService } from '@/app/services/payouts/payouts.service';
import { BriefProgressEnum } from '@/app/common/general-types/briefs.dto';
import { noProductSortOptions } from '@/app/common/app-config/filter-models/sort';
import { countriesList } from '@/app/common/app-config/filter-models/countries';
import { ImageService } from '@/app/services/image/image.service';
import { CurrencyPipe, DatePipe, JsonPipe } from '@angular/common';
import { ToastNotificationService } from '@/app/atoms/toast-notification/toast-notification.service';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { AirwallexTransactionDetails } from '../../atoms/airwallex-transaction-details/airwallex-transaction-details';
import { TooltipDirective } from '@/app/common/directives/tooltip/tooltip.directive';
import { CopyId } from '@/app/atoms/copy-id/copy-id';
import { buildAirwallexConfirmationFilename } from './airwallex-confirmation-filename';

@Component({
  selector: 'admin-payouts',
  imports: [FormField, DatePipe, CurrencyPipe, JsonPipe, AirwallexTransactionDetails, TooltipDirective, CopyId],
  templateUrl: './payouts.html',
  styleUrl: './payouts.scss',
})
export class Payouts implements OnInit, OnDestroy {

  /** Page size aligns with AdminFilter payout listing defaults (`take: 20`). */
  private readonly payoutsPageSize = 20;

  /** Bumped when filters/search context resets to ignore stale payout HTTP responses (including append). */
  private readonly payoutListGeneration = signal(0);

  payoutsLoadingMore = signal(false);
  payoutsHasMore = signal(true);

  protected readonly PayoutStatusEnum = PayoutStatusEnum;
  selectedTab = signal<PayoutStatusEnum>(PayoutStatusEnum.All);

  private subscriptions = new Subscription();
  searchQueryModel = signal({
    query: '',
  })
  searchQueryForm = form(this.searchQueryModel);

  /** Trimmed applicant search sent as `searchTerm` on payout list requests. */
  readonly payoutSearchTerm = computed(() => this.searchQueryModel().query.trim());

  selectedFilter = signal<PayoutStatusEnum>(PayoutStatusEnum.All);

  pageNumber = signal<number>(0);
  filteredPayouts = signal<PayoutDto[] | undefined>(undefined);
  filtersApplied = signal<FilterCreatorsDto>({
    payoutStatus: PayoutStatusEnum.All,
    countryCode: [],
    type: PayoutTypeEnum.All,
    businessAccounts: [],
    briefApplicationId: '',
  });

  filterStatsSignal = signal<{
    All: number;
    AwaitingApproval: number;
    AwaitingCmApproval: number;
    AwaitingFunds: number;
    Created: number;
    Failed: number,
    ProviderPreparing: number,
    Succeeded: number,
    InsufficientFunds: number
  } | undefined | any>({
    All: 0,
    AwaitingApproval: 0,
    AwaitingCmApproval: 0,
    AwaitingFunds: 0,
    Created: 0,
    Failed: 0,
    ProviderPreparing: 0,
    Succeeded: 0,
    InsufficientFunds: 0
  });

  /** Services */
  private adminFilterService = inject(AdminFilter);
  private payoutsService = inject(PayoutsService);
  public imageService = inject(ImageService);
  public toastNotificationService = inject(ToastNotificationService);
  private alertService = inject(AlertService);
  /** Services - END */

  activePayoutsFilters: WritableSignal<ActiveFiltersModel> = signal({
    payoutStatus: PayoutStatusEnum.All,
    countryOptions: countriesList,
    sortOptions: noProductSortOptions,
    brandOptions: this.payoutsService.brandsFilterOptions,
    briefsOptions: []
  });

  /** Accordion: at most one payout's attempt history is open. */
  expandedPayoutId = signal<string | null>(null);

  /** Which payout attempt are we showing the details for? */
  showAttemptDetailsFor = signal<string[]>([]);

  /** Attempt id currently downloading an Airwallex confirmation letter PDF. */
  downloadingConfirmationFor = signal<string | null>(null);

  constructor() {

    effect(() => {
      console.log('PAY effect(): ', this.adminFilterService.filtersObjectSignal());
      this.pageNumber.set(0);
      this.filteredPayouts.set(undefined);
      this.payoutsHasMore.set(true);
      this.payoutListGeneration.update((g) => g + 1);
      const genSnapshot = this.payoutListGeneration();
      this.filtersApplied.set(this.adminFilterService.filtersObjectSignal());

      this.getFilterStats();

      const filtersSnapshot = this.filtersApplied();
      this.getPayouts(filtersSnapshot, 0, false, genSnapshot);
    });

    const searchSub = toObservable(this.searchQueryModel)
      .pipe(
        map((m) => m.query.trim()),
        debounceTime(300),
        distinctUntilChanged(),
        skip(1),
      )
      .subscribe(() => {
        this.pageNumber.set(0);
        this.filteredPayouts.set(undefined);
        this.payoutsHasMore.set(true);
        this.payoutListGeneration.update((g) => g + 1);
        const genSnapshot = this.payoutListGeneration();
        this.getPayouts(this.filtersApplied(), 0, false, genSnapshot);
      });
    this.subscriptions.add(searchSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.adminFilterService.activeFiltersSignal.set(this.activePayoutsFilters());
  }

  getPayouts(filters: FilterCreatorsDto, page: number, append: boolean, listGeneration: number): void {
    console.log('😎😎😎 getPayouts: ', filters, page, append);

    if (append) {
      if (this.payoutsLoadingMore()) {
        return;
      }
      this.payoutsLoadingMore.set(true);
    }

    const term = this.payoutSearchTerm();
    const payload: FilterCreatorsDto = {
      ...filters,
      skip: page * this.payoutsPageSize,
      take: this.payoutsPageSize,
    };
    if (term.length > 0) {
      payload.searchTerm = term;
    } else {
      delete payload.searchTerm;
    }

    const sub = this.payoutsService.getFancyPayouts(payload)
      .pipe(
        finalize(() => {
          if (append) {
            this.payoutsLoadingMore.set(false);
          }
        })
      )
      .subscribe({
        next: (payouts) => {
          if (listGeneration !== this.payoutListGeneration()) {
            return;
          }

          console.log('😎😎😎 payouts: ', payouts);
          const chunk: PayoutDto[] = payouts.data ?? [];
          const rawTotal =
            typeof payouts.totalCount === 'number'
              ? payouts.totalCount
              : typeof payouts.count === 'number'
                ? payouts.count
                : undefined;

          if (append && this.filteredPayouts()) {
            const existing = this.filteredPayouts()!;
            const merged = [
              ...existing,
              ...chunk.filter((p) => !existing.some((e) => e.id === p.id)),
            ];
            this.filteredPayouts.set(merged);
          } else {
            this.filteredPayouts.set(chunk);
          }

          const listLength = this.filteredPayouts()!.length;
          let hasMorePages: boolean;

          if (typeof rawTotal === 'number') {
            hasMorePages = listLength < rawTotal && chunk.length > 0;
          } else {
            hasMorePages = chunk.length >= this.payoutsPageSize;
          }

          this.payoutsHasMore.set(hasMorePages);
          this.pageNumber.set(page);
        },
      });

    this.subscriptions.add(sub);
  }

  onPayoutTableScroll(event: Event): void {
    const root = event.target;
    if (!(root instanceof HTMLElement)) {
      return;
    }

    const thresholdPx = 120;
    const distanceToBottom =
      root.scrollHeight - root.scrollTop - root.clientHeight;
    if (distanceToBottom > thresholdPx) {
      return;
    }

    this.maybeLoadMorePayouts();
  }

  private maybeLoadMorePayouts(): void {
    if (
      this.filteredPayouts() === undefined ||
      !this.filteredPayouts()?.length ||
      !this.payoutsHasMore() ||
      this.payoutsLoadingMore()
    ) {
      return;
    }

    const nextPage = this.pageNumber() + 1;
    const genSnapshot = this.payoutListGeneration();

    const filtersSnapshot = this.filtersApplied();
    this.getPayouts(filtersSnapshot, nextPage, true, genSnapshot);
  }

  getFilterStats() {
    // console.log('😎😎😎 getFilterStats: ');
    const sub = this.payoutsService.getFilterStats().subscribe((stats: any) => {
      // console.log('😎😎😎 stats: ', stats);
      this.filterStatsSignal.set(stats);
    });
    this.subscriptions.add(sub);
  }

  updateSelectedFilter(filter: PayoutStatusEnum) {
    console.log('updateSelectedFilter(): ', filter);
    this.selectedTab.set(filter);

    this.selectedFilter.set(filter);

    this.adminFilterService.filtersObjectSignal.set({
      ...this.adminFilterService.filtersObjectSignal(),
      payoutStatus: filter
    });
  }

  getTabClass(tab: PayoutStatusEnum) {
    return this.selectedTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  isAttemptsExpanded(payout: PayoutDto): boolean {
    return !!payout.id && this.expandedPayoutId() === payout.id;
  }

  expandAttempts(payout: PayoutDto): void {
    const id = payout.id;
    if (!id) {
      return;
    }
    if (this.expandedPayoutId() === id) {
      this.expandedPayoutId.set(null);
      return;
    }
    this.expandedPayoutId.set(id);
    this.showAttemptDetailsFor.set([]);
  }

  attemptsPanelClass(payout: PayoutDto): string {
    const base =
      'grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none';
    return this.isAttemptsExpanded(payout)
      ? `${base} grid-rows-[1fr]`
      : `${base} grid-rows-[0fr]`;
  }

  getStatusColour(payoutStatus: PayoutStatusEnum) {
    switch (payoutStatus) {
      case PayoutStatusEnum.Created:
      case PayoutStatusEnum.Sent:
        return 'pill-blue';
      case PayoutStatusEnum.Succeeded:
        return 'pill-green';
      case PayoutStatusEnum.ProviderPreparing:
      case PayoutStatusEnum.AwaitingCmApproval:
      case PayoutStatusEnum.AwaitingApproval:
      case PayoutStatusEnum.AwaitingFunds:
      case PayoutStatusEnum.AwaitingUserConfirmation:
      case PayoutStatusEnum.InsufficientFunds:
        return 'pill-amber';
      case PayoutStatusEnum.Failed:
        return 'pill-red';
      default:
        return 'pill-gray';
    }
  }

  getPayoutAttemptStatus(payoutStatus: PayoutStatusEnum) {
    switch (payoutStatus) {
      case PayoutStatusEnum.Created:
        return 'Created';
      case PayoutStatusEnum.Succeeded:
        return 'Succeeded';
      case PayoutStatusEnum.ProviderPreparing:
        return 'Preparing';
      case PayoutStatusEnum.Failed:
        return 'Failed';
      case PayoutStatusEnum.AwaitingCmApproval:
        return 'CM App.';
      case PayoutStatusEnum.AwaitingFunds:
        return 'Awaiting Funds';
      case PayoutStatusEnum.AwaitingUserConfirmation:
        return 'Await App.';
      case PayoutStatusEnum.InsufficientFunds:
        return 'Insufficient Funds';
      case PayoutStatusEnum.Sent:
        return 'Sent to provider';
      default:
        return 'All';
    }
  }

  hasSucceededOrProcessing(payoutStatus: PayoutStatusEnum) {
    return payoutStatus == PayoutStatusEnum.Succeeded || payoutStatus == PayoutStatusEnum.ProviderPreparing;
  }

  private patchPayoutInList(payoutId: string, patch: Partial<PayoutDto>): void {
    this.filteredPayouts.update((list) => {
      if (!list) {
        return list;
      }
      return list.map((p) => (p.id === payoutId ? { ...p, ...patch } : p));
    });
  }

  private setPayoutProcessing(payoutId: string, isProcessing: boolean): void {
    this.patchPayoutInList(payoutId, { isProcessing });
  }

  async retryPayout(payout: PayoutDto, _index: number): Promise<void> {
    const payoutId = payout.id;
    if (!payoutId || payout.isProcessing) {
      return;
    }

    const isApprove = payout.payoutStatus === PayoutStatusEnum.AwaitingCmApproval;
    const confirmed = await this.alertService.confirm({
      title: isApprove ? 'Approve payout?' : 'Retry payment?',
      message: isApprove
        ? 'This will approve and process the payout. Continue?'
        : 'This will retry the payment for this payout. Continue?',
      confirmText: isApprove ? 'Approve' : 'Retry payment',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });
    if (!confirmed) {
      return;
    }

    this.setPayoutProcessing(payoutId, true);

    this.subscriptions.add(
      this.payoutsService.retryPayout(payoutId).subscribe({
        next: () => {
          this.toastNotificationService.alert({
            title: isApprove ? 'Payout Approved' : 'Payout Retried',
            message: isApprove
              ? 'The payout has been approved successfully.'
              : 'The payout has been retried successfully.',
            type: 'success',
            icon: 'success',
          });
          this.getFilterStats();
          this.getPayouts(
            this.filtersApplied(),
            this.pageNumber(),
            false,
            this.payoutListGeneration(),
          );
        },
        error: () => {
          this.setPayoutProcessing(payoutId, false);
          this.toastNotificationService.alert({
            title: isApprove ? 'Error Approving Payout' : 'Error Retrying Payout',
            message: isApprove
              ? 'Something went wrong while approving the payout. Please try again.'
              : 'Something went wrong while retrying the payout. Please try again.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }

  attemptIncludesJson(failedReason: string) {
    return failedReason && failedReason.includes('{') && failedReason.includes('}');
  }

  getJsonPortion(failedReason: string) {
    const jsonPortion = failedReason.split("|")[1].trim();
    return JSON.parse(jsonPortion);
  }

  getJsonLabel(failedReason: string) {
    const jsonPortion = failedReason.split("|")[0].trim();
    return jsonPortion;
  }

  showAttemptDetails(attemptId: string) {
    if (this.showAttemptDetailsFor().indexOf(attemptId) == -1) {
      this.showAttemptDetailsFor.set([...this.showAttemptDetailsFor(), attemptId]);
    } else {
      this.showAttemptDetailsFor.set(this.showAttemptDetailsFor().filter((id) => id !== attemptId));
    }
  }

  downloadConfirmationLetter(
    transferId: string,
    attemptId: string,
    payout: PayoutDto,
    event?: Event,
  ): void {
    event?.stopPropagation();
    if (!transferId || !attemptId || !payout.id || this.downloadingConfirmationFor()) {
      return;
    }

    this.downloadingConfirmationFor.set(attemptId);
    this.subscriptions.add(
      this.payoutsService.downloadAirwallexConfirmationLetter(transferId).subscribe({
        next: (blob) => {
          this.downloadingConfirmationFor.set(null);
          const url = URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = buildAirwallexConfirmationFilename({
            transferId,
            payoutId: payout.id!,
            date: payout.createdDate,
            firstName: payout.creator?.firstName,
            lastName: payout.creator?.lastName,
          });
          anchor.click();
          URL.revokeObjectURL(url);
        },
        error: () => {
          this.downloadingConfirmationFor.set(null);
          this.toastNotificationService.alert({
            title: 'Download failed',
            message: 'Could not download the Airwallex transfer confirmation.',
            type: 'error',
            icon: 'error',
          });
        },
      }),
    );
  }
}
